import * as React from 'react';
import { cn } from '../../utils/cn';
import { Avatar, AvatarImage, AvatarFallback } from '../avatar';
import { avatarVariants } from '../avatar/avatar';
import { type VariantProps } from 'class-variance-authority';

// Define interfaces for our component
interface Avatar {
    imageUrl?: string;
    name?: string;
}

/**
 * AvatarVariantProps is the type for the avatar props using the actual props from the component
 */
type AvatarVariantProps = VariantProps<typeof avatarVariants>;

/**
 * AvatarCirclesProps is the type for the avatar circles props
 */
export interface AvatarCirclesProps {
    className?: string;
    avatarUrls: Avatar[];
    size?: AvatarVariantProps['size'];
    borderWidth?: AvatarVariantProps['borderWidth'];
    borderColor?: AvatarVariantProps['borderColor'];
    maxAvatars?: number;
}

export const AvatarCircles = ({
    className,
    avatarUrls,
    size = 'square40',
    borderWidth = 'small',
    borderColor = 'light',
    maxAvatars = 3,
}: AvatarCirclesProps) => {
    const avatarCount = avatarUrls.length;
    const remainingAvatars = avatarCount - maxAvatars;

    return (
        <div
            className={cn(
                'z-10 flex -space-x-3 rtl:space-x-reverse',
                className
            )}
        >
            {avatarUrls.slice(0, maxAvatars).map((url, index) => (
                <Avatar
                    key={index}
                    size={size}
                    borderWidth={borderWidth}
                    borderColor={borderColor}
                >
                    <AvatarImage
                        src={url.imageUrl}
                        alt={url.name || `Avatar ${index + 1}`}
                        fit="cover"
                    />
                    <AvatarFallback size={mapFallBackTextSize(size)}>
                        {url.name ? getInitials(url.name) : `${index + 1}`}
                    </AvatarFallback>
                </Avatar>
            ))}
            {(remainingAvatars ?? 0) > 0 && (
                <Avatar
                    size={size}
                    borderWidth={borderWidth}
                    borderColor={borderColor}
                >
                    <AvatarFallback size={mapFallBackTextSize(size)}>
                        +{remainingAvatars}
                    </AvatarFallback>
                </Avatar>
            )}
        </div>
    );
};

// Helper function to get initials from a name
const getInitials = (name: string): string => {
    return name
        .split(' ')
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

const mapFallBackTextSize = (
    size: AvatarVariantProps['size']
): 'xs' | 'sm' | 'md' | 'default' => {
    switch (size) {
        case 'square20':
            return 'xs';
        case 'square24':
            return 'xs';
        case 'square28':
            return 'sm';
        case 'square32':
            return 'md';
        case 'square40':
            return 'default';
        case 'square56':
            return 'default';
        case 'square64':
            return 'default';
        case 'square72':
            return 'default';
        default:
            return 'default';
    }
};
